import { api } from "./api";
import {
  ServiceInstance,
  CreateInstanceRequest,
  InstanceLogsResponse,
  CredentialsResponse,
  UserResourceStats,
} from "../types";

export const MAX_ACTIVE_INSTANCES = 3;

export const instanceService = {
  async create(request: CreateInstanceRequest): Promise<ServiceInstance> {
    const response = await api.post<ServiceInstance>("/instances", request);
    return response.data;
  },

  async getById(id: number): Promise<ServiceInstance> {
    const response = await api.get<ServiceInstance>(`/instances/${id}`);
    return response.data;
  },

  async getByInstanceId(instanceId: string): Promise<ServiceInstance> {
    const response = await api.get<ServiceInstance>(
      `/instances/by-instance-id/${instanceId}`,
    );
    return response.data;
  },

  async getByUser(
    userId: number,
    page = 0,
    size = 20,
  ): Promise<ServiceInstance[]> {
    const response = await api.get<ServiceInstance[]>(
      `/instances/user/${userId}`,
      { page, size },
    );
    return response.data;
  },

  async getActiveByUser(userId: number): Promise<ServiceInstance[]> {
    const response = await api.get<ServiceInstance[]>(
      `/instances/user/${userId}/active`,
    );
    return response.data;
  },

  async getActiveCount(userId: number): Promise<number> {
    const response = await api.get<number>(`/instances/user/${userId}/count`);
    return response.data;
  },

  async terminate(instanceId: string): Promise<void> {
    await api.post<void>(`/instances/${instanceId}/terminate`);
  },

  async deleteInstance(instanceId: string): Promise<void> {
    await api.delete<void>(`/instances/${instanceId}`);
  },

  async stop(instanceId: string): Promise<ServiceInstance> {
    const response = await api.post<ServiceInstance>(
      `/instances/${instanceId}/stop`,
    );
    return response.data;
  },

  async start(instanceId: string): Promise<ServiceInstance> {
    const response = await api.post<ServiceInstance>(
      `/instances/${instanceId}/start`,
    );
    return response.data;
  },

  async restart(instanceId: string): Promise<ServiceInstance> {
    const response = await api.post<ServiceInstance>(
      `/instances/${instanceId}/restart`,
    );
    return response.data;
  },

  async getLogs(
    instanceId: string,
    lines = 100,
  ): Promise<InstanceLogsResponse> {
    const response = await api.get<InstanceLogsResponse>(
      `/instances/${instanceId}/logs`,
      { lines },
    );
    return response.data;
  },

  async getCompose(
    instanceId: string,
  ): Promise<{ instanceId: string; yaml: string }> {
    const response = await api.get<{ instanceId: string; yaml: string }>(
      `/instances/${instanceId}/compose`,
    );
    return response.data;
  },

  async getMyStats(): Promise<UserResourceStats> {
    const response = await api.get<UserResourceStats>("/instances/my/stats");
    return response.data;
  },

  async getCredentials(instanceId: string): Promise<CredentialsResponse> {
    const response = await api.get<CredentialsResponse>(
      `/instances/${instanceId}/credentials`,
    );
    return response.data;
  },

  isActive(instance: ServiceInstance): boolean {
    return ["PENDING", "PROVISIONING", "RUNNING", "STOPPED"].includes(
      instance.status,
    );
  },

  isRunning(instance: ServiceInstance): boolean {
    return instance.status === "RUNNING";
  },

  isStopped(instance: ServiceInstance): boolean {
    return instance.status === "STOPPED";
  },

  canStop(instance: ServiceInstance): boolean {
    return instance.status === "RUNNING";
  },

  canStart(instance: ServiceInstance): boolean {
    return instance.status === "STOPPED";
  },

  canRestart(instance: ServiceInstance): boolean {
    return instance.status === "RUNNING";
  },

  isExpired(instance: ServiceInstance): boolean {
    return new Date(instance.expiresAt) < new Date();
  },

  getTimeRemaining(instance: ServiceInstance): {
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
    isExpired: boolean;
  } {
    const now = new Date().getTime();
    const expiresAt = new Date(instance.expiresAt).getTime();
    const total = expiresAt - now;

    if (total <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, total: 0, isExpired: true };
    }

    const hours = Math.floor(total / (1000 * 60 * 60));
    const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((total % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, total, isExpired: false };
  },

  formatTimeRemaining(instance: ServiceInstance): string {
    const { hours, minutes, isExpired } = this.getTimeRemaining(instance);
    if (isExpired) return "Expired";
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return days + "d " + (hours % 24) + "h";
    }
    return hours + "h " + minutes + "m";
  },

  canCreateInstance(activeCount: number): boolean {
    return activeCount < MAX_ACTIVE_INSTANCES;
  },
};
